/**
 * Sign-in — the same three ways in the Studio has, on the design system's `SignInPanel`.
 *
 * Signing in does **not** get you into the app: it gets you a token, and the token either
 * carries the `admin` claim or it does not. That is why this surface says nothing about
 * admins — the gate does, afterwards, in words a person can act on. What lives here is the
 * admin app's side of the panel: its English copy, the provider's actions, and the error
 * matrix (`errors.ts`) as lines.
 */

import { Card, SignInPanel, Wordmark } from '@atlas-nook/design-system';
import type { SignInActions, SignInProblem, SignInStrings } from '@atlas-nook/design-system';
import { useMemo } from 'react';
import type { JSX } from 'react';
import { Navigate, useLocation } from 'react-router';

import { adminEnv, EMULATOR } from '../env.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { useAuth } from './AuthProvider.js';
import { classifyAuthError } from './errors.js';
import type { AuthProblemKind } from './errors.js';

const PROBLEM_COPY: Record<Exclude<AuthProblemKind, 'accountExists'>, string> = {
  badCredentials: S.signin.errBadCredentials,
  emailInUse: S.signin.errEmailInUse,
  weakPassword: S.signin.errWeakPassword,
  tooMany: S.signin.errTooMany,
  network: S.signin.errNetwork,
  invalidEmail: S.signin.errInvalidEmail,
  popup: S.signin.errPopup,
  methodOff: S.signin.errMethodOff,
  recentLogin: S.signin.errRecentLogin,
  failed: S.signin.failed,
};

export function describeAuthError(error: unknown): SignInProblem {
  const problem = classifyAuthError(error);
  if (problem.kind === 'accountExists') {
    const method = problem.method ?? 'password';
    return {
      message:
        method === 'link' ? S.signin.errAccountExistsLink : S.signin.errAccountExistsPassword,
      offer: method,
      ...(problem.email !== undefined && problem.email !== '' ? { email: problem.email } : {}),
    };
  }
  return { message: PROBLEM_COPY[problem.kind] };
}

const STRINGS: SignInStrings = {
  title: S.signin.title,
  lead: S.signin.lead,
  email: S.signin.email,
  emailPlaceholder: S.signin.emailPlaceholder,
  emailRequired: S.signin.emailRequired,
  sendLink: S.signin.send,
  sending: S.signin.sending,
  usePassword: S.signin.usePassword,
  password: S.signin.password,
  passwordRequired: S.signin.passwordRequired,
  passwordTooShort: S.signin.passwordTooShort,
  signIn: S.signin.signIn,
  create: S.signin.create,
  forgot: S.signin.forgot,
  working: S.signin.working,
  or: S.signin.or,
  google: S.signin.google,
  sentTitle: S.signin.sentTitle,
  sentBody: S.signin.sentBody,
  sentAgain: S.signin.sentAgain,
  resetTitle: S.signin.resetTitle,
  resetBody: S.signin.resetBody,
  back: S.signin.back,
  confirmTitle: S.signin.confirmEmailTitle,
  confirmBody: S.signin.confirmEmailBody,
  confirmAction: S.signin.confirmEmailAction,
  finishing: S.signin.finishing,
  offerPassword: S.signin.offerPassword,
  offerLink: S.signin.offerLink,
};

/** Where to land after signing in: where the person was going, else the catalog. */
function useDestination(): string {
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && from !== '/signin' ? from : '/mediums';
}

export function SignIn(): JSX.Element {
  const auth = useAuth();
  const destination = useDestination();
  const useEmulator = adminEnv().useEmulator;

  const strings = useMemo<SignInStrings>(
    () => ({
      ...STRINGS,
      ...(useEmulator ? { emulator: S.signin.emulator(EMULATOR.authUrl) } : {}),
    }),
    [useEmulator],
  );

  const actions = useMemo<SignInActions>(
    () => ({
      sendLink: auth.sendLink,
      completeLink: auth.completeLink,
      signInWithPassword: auth.signInWithPassword,
      createAccount: auth.createAccount,
      resetPassword: auth.resetPassword,
      signInWithGoogle: auth.signInWithGoogle,
      describe: async (error) => describeAuthError(error),
    }),
    [auth],
  );

  if (auth.session !== undefined && auth.session !== null) {
    return <Navigate to={destination} replace />;
  }

  return (
    <main
      className="surface surface-center"
      id="main"
      style={{ minHeight: '100vh', overflow: 'auto' }}
    >
      <Card raised>
        <SignInPanel
          strings={strings}
          actions={actions}
          isLinkArrival={auth.isLinkArrival}
          pendingEmail={auth.pendingEmail}
        >
          <Wordmark name={S.app.name} descriptor={S.app.descriptor} />
        </SignInPanel>
      </Card>
    </main>
  );
}
